import React from 'react';
import {
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const CodeBlock = styled('pre')(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  fontSize: '0.875rem',
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
  overflow: 'auto',
  margin: theme.spacing(1, 0),
  border: `1px solid ${theme.palette.grey[300]}`,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}));

const InlineCode = styled('code')(({ theme }) => ({
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(0.25, 0.5),
  borderRadius: '4px',
  fontSize: '0.875em',
  fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
  color: theme.palette.text.primary,
}));

const StyledBlockquote = styled('blockquote')(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  paddingLeft: theme.spacing(2),
  margin: theme.spacing(1, 0),
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
}));

interface MarkdownRendererProps {
  content: string;
  variant?: 'body1' | 'body2';
  sx?: any;
}

// List-item marker patterns — single source of truth for detection + rendering.
// Two forms intentionally differ:
//   *_ITEM — matches an already-trimmed line (used while rendering).
//   *_LINE — indentation-tolerant + multiline (used for content detection so
//            indented/streamed lists still route to the renderer instead of the
//            plain-text fallback that showed literal "1. 1. 1." — issue #781).
const UNORDERED_ITEM = /^[-*]\s+/;
const ORDERED_ITEM = /^\d+\.\s+/;
const UNORDERED_LINE = /^[ \t]*[-*]\s+/m;
const ORDERED_LINE = /^[ \t]*\d+\.\s+/m;

// Hoisted to module scope so the array + regex literals are allocated once,
// not on every call (isMarkdownContent runs per chat message, per render).
const MARKDOWN_PATTERNS: RegExp[] = [
  /^#{1,6}\s+/m,
  UNORDERED_LINE,
  ORDERED_LINE,
  /\*\*.*\*\*/,
  /\*.*\*/,
  /`.*`/,
  /```[\s\S]*```/,
  /^>\s+/m,
  /\[.*\]\(.*\)/,
  /\n---+\n/,
];

/**
 * Detect if content contains markdown syntax
 */
export const isMarkdownContent = (content: string): boolean =>
  MARKDOWN_PATTERNS.some(pattern => pattern.test(content));

/**
 * Format inline markdown: bold, italic, code, links
 */
function formatInline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;

  while (remaining.length > 0) {
    // Bold: **text**
    const boldMatch = remaining.match(/^(.*?)\*\*(.+?)\*\*(.*)/s);
    if (boldMatch) {
      if (boldMatch[1]) parts.push(<span key={key++}>{formatInline(boldMatch[1])}</span>);
      parts.push(<strong key={key++}>{formatInline(boldMatch[2])}</strong>);
      remaining = boldMatch[3];
      continue;
    }

    // Italic: *text*
    const italicMatch = remaining.match(/^(.*?)\*(.+?)\*(.*)/s);
    if (italicMatch) {
      if (italicMatch[1]) parts.push(<span key={key++}>{italicMatch[1]}</span>);
      parts.push(<em key={key++}>{italicMatch[2]}</em>);
      remaining = italicMatch[3];
      continue;
    }

    // Inline code: `text`
    const codeMatch = remaining.match(/^(.*?)`(.+?)`(.*)/s);
    if (codeMatch) {
      if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
      parts.push(<InlineCode key={key++}>{codeMatch[2]}</InlineCode>);
      remaining = codeMatch[3];
      continue;
    }

    // Link: [text](url)
    const linkMatch = remaining.match(/^(.*?)\[(.+?)\]\((.+?)\)(.*)/s);
    if (linkMatch) {
      if (linkMatch[1]) parts.push(<span key={key++}>{linkMatch[1]}</span>);
      parts.push(
        <a key={key++} href={linkMatch[3]} target="_blank" rel="noopener noreferrer"
           style={{ color: 'inherit', textDecoration: 'underline' }}>
          {linkMatch[2]}
        </a>
      );
      remaining = linkMatch[4];
      continue;
    }

    parts.push(<span key={key++}>{remaining}</span>);
    break;
  }

  return parts.length === 1 ? parts[0] : <>{parts}</>;
}

/**
 * Built-in Markdown renderer (no external dependency).
 * Handles headings, lists, bold, italic, code blocks, inline code,
 * blockquotes, horizontal rules, and links.
 */
const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({
  content,
  variant = 'body2',
  sx = {},
}) => {
  const elements: React.ReactNode[] = [];
  const lines = content.split('\n');
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Code block: ```...```
    if (trimmed.startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].trim().startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      elements.push(
        <CodeBlock key={key++}>
          <code>{codeLines.join('\n')}</code>
        </CodeBlock>
      );
      i++; // skip closing ```
      continue;
    }

    // Horizontal rule
    if (/^---+$/.test(trimmed) || /^\*\*\*+$/.test(trimmed)) {
      elements.push(<Divider key={key++} sx={{ my: 2 }} />);
      i++;
      continue;
    }

    // Headings
    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const headingVariant = level <= 2 ? 'h6' : 'subtitle1';
      const fontSize = level === 1 ? '1.25rem' : level === 2 ? '1.1rem' : level === 3 ? '1rem' : '0.9rem';
      elements.push(
        <Typography key={key++} variant={headingVariant as any} sx={{ fontWeight: 600, mt: 2, mb: 0.5, fontSize }}>
          {formatInline(headingMatch[2])}
        </Typography>
      );
      i++;
      continue;
    }

    // Blockquote
    if (trimmed.startsWith('> ')) {
      const quoteLines: string[] = [trimmed.slice(2)];
      i++;
      while (i < lines.length && lines[i].trim().startsWith('> ')) {
        quoteLines.push(lines[i].trim().slice(2));
        i++;
      }
      elements.push(
        <StyledBlockquote key={key++}>
          <Typography variant={variant}>{formatInline(quoteLines.join(' '))}</Typography>
        </StyledBlockquote>
      );
      continue;
    }

    // Unordered list (handles blank lines between items)
    if (UNORDERED_ITEM.test(trimmed)) {
      const listItems: string[] = [];
      while (i < lines.length) {
        const cur = lines[i].trim();
        if (UNORDERED_ITEM.test(cur)) {
          listItems.push(cur.replace(UNORDERED_ITEM, ''));
          i++;
        } else if (cur === '' && i + 1 < lines.length && UNORDERED_ITEM.test(lines[i + 1].trim())) {
          i++;
        } else {
          break;
        }
      }
      elements.push(
        <Box key={key++} component="ul" sx={{ pl: 3, my: 0.5 }}>
          {listItems.map((item, idx) => (
            <li key={idx}>
              <Typography variant={variant} sx={{ m: 0 }}>{formatInline(item)}</Typography>
            </li>
          ))}
        </Box>
      );
      continue;
    }

    // Ordered list (handles blank lines between items, e.g. "1.\n\n1.\n\n1.")
    if (ORDERED_ITEM.test(trimmed)) {
      const listItems: string[] = [];
      while (i < lines.length) {
        const cur = lines[i].trim();
        if (ORDERED_ITEM.test(cur)) {
          listItems.push(cur.replace(ORDERED_ITEM, ''));
          i++;
        } else if (cur === '' && i + 1 < lines.length && ORDERED_ITEM.test(lines[i + 1].trim())) {
          // Skip blank line between ordered list items
          i++;
        } else {
          break;
        }
      }
      elements.push(
        <Box key={key++} component="ol" sx={{ pl: 3, my: 0.5 }}>
          {listItems.map((item, idx) => (
            <li key={idx}>
              <Typography variant={variant} sx={{ m: 0 }}>{formatInline(item)}</Typography>
            </li>
          ))}
        </Box>
      );
      continue;
    }

    // Empty line
    if (trimmed === '') {
      i++;
      continue;
    }

    // Regular paragraph
    elements.push(
      <Typography key={key++} variant={variant} paragraph sx={{ mb: 1 }}>
        {formatInline(trimmed)}
      </Typography>
    );
    i++;
  }

  return <Box sx={{ ...sx }}>{elements}</Box>;
};

export default MarkdownRenderer;
